/**
 * Domain lists for different organization types.
 * Note: Empty strings or arrays are not supported - use data.
 * @type {string[]}
 */
const NHS_DOMAINS = ['nhs', 'hscni'];
const GOV_DOMAINS = ['gov'];
const CHARITY_DOMAINS = [
    'andovermind.org.uk'
];
const CARE_HOME_DOMAINS = [
    'bupa',
    'agecare.uk.com',
    'mha.org.uk',
    'mmcgcarehomes.co.uk',
    'priorygroup.com'
];
const OTHER_BUSINESS_DOMAINS = [
    'medequip-uk.com',
    'humanprotection.nl',
    'murrays.ie',
    'alzstoreuk.com',
    'it-fits.nl',
    'joyandcare.com',
    'doctors.net.uk'
];

/**
 * Maps domain types to customer categories.
 * @type {object}
 */
export const CUSTOMER_TYPES = {
    NHS: 'NHS',
    GOV: 'Government',
    CARE_HOME: 'Healthcare - Care Home',
    CHARITY: 'Charity',
    OTHER_BUSINESS: 'Business Customer',
    DEFAULT: 'Other Customer',
    PERSONAL: 'Personal'
};

/**
 * Determines if a customer type is considered a credit customer (NHS and Government customers).
 * @param {string} customerType - The customer type to check.
 * @returns {boolean} True if the customer is a credit customer.
 */
export function isCreditCustomer(customerType) {
    return customerType === CUSTOMER_TYPES.NHS || customerType === CUSTOMER_TYPES.GOV;
}

/**
 * Gets customer information from email address.
 * @param {string} email - Email address to process.
 * @returns {DomainInfo|null} Customer information or null if email invalid.
 */
export function getCustomerInfo(email) {
    if (!isValidEmail(email)) return null;

    const domain = extractDomain(email);
    return getDomainInfo(domain);
}

/**
 * @typedef {object} DomainInfo
 * @property {boolean} isHighValue - Whether the domain is considered high value.
 * @property {string} customerType - The type of customer based on domain.
 * @property {string} domain - The domain to evaluate and classify.
 */

/**
 * Gets domain information for a given domain.
 * @param {string} domain - The domain to analyze.
 * @returns {DomainInfo} Domain information object.
 */
function getDomainInfo(domain) {
    if (checkDomain(domain, NHS_DOMAINS)) {
        return {
            isHighValue: true,
            customerType: CUSTOMER_TYPES.NHS,
            domain: domain
        };
    }
    if (checkDomain(domain, GOV_DOMAINS)) {
        return {
            isHighValue: true,
            customerType: CUSTOMER_TYPES.GOV,
            domain: domain
        };
    }
    if (checkDomain(domain, CARE_HOME_DOMAINS)) {
        return {
            isHighValue: true,
            customerType: CUSTOMER_TYPES.CARE_HOME,
            domain: domain
        };
    }
    if (checkDomain(domain, CHARITY_DOMAINS)) {
        return {
            isHighValue: true,
            customerType: CUSTOMER_TYPES.CHARITY,
            domain: domain
        };
    }
    if (checkDomain(domain, OTHER_BUSINESS_DOMAINS)) {
        return {
            isHighValue: true,
            customerType: CUSTOMER_TYPES.OTHER_BUSINESS,
            domain: domain
        };
    }

    return {
        isHighValue: false,
        customerType: CUSTOMER_TYPES.DEFAULT,
        domain: domain
    };
}

/**
 * Validates an email address has basic correct format.
 * @param {string} email - Email address to validate.
 * @returns {boolean} True if email appears valid.
 */
function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;

    // Checks for: some characters, then @, then some characters, then dot, then some characters
    const emailRegex = /^\S+@\S+\.\S+$/;
    return emailRegex.test(email.trim());
}

/**
 * Extracts domain portion from an email address.
 * @param {string} email - Email address to process.
 * @returns {string|null} Domain part of email or null if invalid.
 */
function extractDomain(email) {

    const parts = email.split('@');
    const domain = parts[parts.length - 1].toLowerCase().trim();

    if (!domain) return '';

    return domain;
}


/**
 * Checks if a given domain is valid by verifying if it matches any of the allowed domains.
 * @param {string} domain - The domain to check.
 * @param {string[]} allowedDomains - An array of allowed domain strings to validate against.
 * @returns {boolean} Returns true if the domain matches any of the allowed domains, otherwise returns false.
 */
function checkDomain(domain, allowedDomains) {
    return allowedDomains.some(value => domain.includes(value));
}
